import { API_BASE } from '@/shared/services/api';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Settings, LogOut, Moon, Sun, Bell, BellOff, Globe, Smartphone } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger } from
'@/shared/components/ui/tooltip';
import { NotificationCenter } from './NotificationCenter';
import { GlobalSearch } from './GlobalSearch';
import { MobilePreviewPanel } from '@/shared/components/MobilePreviewPanel';
import { BusinessSwitcher } from '@/shared/components/BusinessSelector';
import { useAuth } from '@/shared/contexts/AuthContext';
import { usePushNotifications } from '@/shared/contexts/PushNotificationContext';
const API_URL = API_BASE;

export const Header = () => {
  const { t } = useTranslation(['common', 'settings']);
  const [isDark, setIsDark] = useState(false);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotifications();

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
  };

  const handlePushToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card px-6 shadow-sm">
        {/* Business Selector - Left side */}
        <div className="flex items-center gap-4">
          <BusinessSwitcher />
        </div>

        {/* Search - Center */}
        <div className="flex-1 max-w-md mx-4">
          <GlobalSearch />
        </div>

        <div className="flex items-center gap-3">
          {/* Mobile Preview Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobilePreviewOpen(true)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="mobile-preview-btn">
                  
                  <Smartphone className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("mobilePreview", "Mobile Preview")}

              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground"
            data-testid="theme-toggle">
            
            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {isSupported &&
          <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePushToggle}
                  disabled={isLoading}
                  className={`text-muted-foreground hover:text-foreground ${isSubscribed ? 'text-green-500' : ''}`}
                  data-testid="push-notification-toggle">
                  
                    {isSubscribed ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isSubscribed ? 'Push notifications enabled' : 'Enable push notifications'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }

          <NotificationCenter />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-muted" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  {user?.photo_url ?
                  <AvatarImage src={`${API_URL}${user.photo_url}`} /> :

                  <AvatarImage src={`https://i.pravatar.cc/150?u=${user?.email}`} />
                  }
                  <AvatarFallback>
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || 'user@ezparcel.com'}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{t('common:myAccount')}</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <Link to="/settings/user">
                <DropdownMenuItem className="cursor-pointer">
                  <Globe className="mr-2 h-4 w-4" />
                  <span>{t('settings:title')}</span>
                </DropdownMenuItem>
              </Link>
              <Link to="/settings">
                <DropdownMenuItem className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>{t('common:systemSettings')}</span>
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t('common:logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Preview Panel */}
      <MobilePreviewPanel
        isOpen={isMobilePreviewOpen}
        onClose={() => setIsMobilePreviewOpen(false)} />
      
    </>);

};